import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Alert,
} from '@mui/material';
import { Category, Transaction } from '../types';
import SmartSuggestions from './SmartSuggestions';
import { smartCategorization } from '../services/smartCategorization';

interface TransactionFormProps {
  categories: Category[];
  transactions: Transaction[];
  onSubmit: (transaction: Omit<Transaction, 'id'>) => void | Promise<void>;
}

const TransactionForm: React.FC<TransactionFormProps> = ({ categories, transactions, onSubmit }) => {
  const [formData, setFormData] = useState({
    type: 'expense' as 'income' | 'expense',
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    recurring: false,
    recurringPeriod: 'monthly' as 'monthly' | 'yearly',
  });
  const [success, setSuccess] = useState(false);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.category || !formData.amount || !formData.description) {
      return;
    }

    const transaction: Omit<Transaction, 'id'> = {
      type: formData.type,
      category: formData.category,
      amount: parseFloat(formData.amount),
      description: formData.description,
      date: new Date(formData.date),
      recurring: formData.recurring,
      recurringPeriod: formData.recurring ? formData.recurringPeriod : undefined,
    };

    // Apprendre de cette transaction pour améliorer les suggestions futures
    if (formData.description && formData.category) {
      smartCategorization.learnFromTransaction(formData.description, formData.category);
    }
    
    await onSubmit(transaction);
    setSuccess(true);
    setFormData({
      type: 'expense',
      category: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      recurring: false,
      recurringPeriod: 'monthly',
    });

    setTimeout(() => setSuccess(false), 3000);
  };

  const filteredCategories = categories.filter(cat => cat.type === formData.type);

  // Callback pour gérer les suggestions de l'IA (fonctionnalité future)
  // const handleCategorySuggestion = (category: string, confidence: number) => {
  //   // Fonctionnalité à implémenter
  // };

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Ajouter une transaction
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Transaction ajoutée avec succès !
        </Alert>
      )}

      {/* Composant d'intelligence artificielle */}
      <SmartSuggestions 
        transactions={transactions}
        currentDescription={formData.description}
        onCategorySuggestion={() => {}} // Fonctionnalité future
      />

      <Box component="form" onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal">
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as 'income' | 'expense', category: '' })}
            label="Type"
          >
            <MenuItem value="income">Revenu</MenuItem>
            <MenuItem value="expense">Dépense</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Catégorie</InputLabel>
          <Select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            label="Catégorie"
          >
            {filteredCategories.map(cat => (
              <MenuItem key={cat.id} value={cat.name}>
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label="Montant (€)"
          type="number"
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          inputProps={{ step: '0.01', min: '0' }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Date"
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.recurring}
              onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
            />
          }
          label="Transaction récurrente"
        />

        {formData.recurring && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Période de récurrence</InputLabel>
            <Select
              value={formData.recurringPeriod}
              onChange={(e) => setFormData({ ...formData, recurringPeriod: e.target.value as 'monthly' | 'yearly' })}
              label="Période de récurrence"
            >
              <MenuItem value="monthly">Mensuelle</MenuItem>
              <MenuItem value="yearly">Annuelle</MenuItem>
            </Select>
          </FormControl>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{ mt: 3, mb: 2 }}
          fullWidth
        >
          Ajouter la transaction
        </Button>
      </Box>
    </Paper>
  );
};

export default TransactionForm;